import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TagInput from '../components/study/TagInput';
import { studyApi, templateApi } from '../utils/studyApiService';
import './StudyFormPage.css';

const StudyFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // 수정 모드이면 id 존재
  const isEdit = !!id;

  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await templateApi.list();
      if (res.success) {
        const list = res.data.items || res.data || [];
        setTemplates(list);
      }
    } catch (_) {}
  }, []);

  const loadExistingStudy = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await studyApi.get(id);
      if (res.success) {
        const s = res.data;
        setSelectedTemplateId(s.template?.id || '');
        setTitle(s.title || '');
        setDescription(s.description || '');
        setTags(s.tags || []);
      }
    } catch (e) {
      setError('스터디를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTemplates();
    loadExistingStudy();
  }, [fetchTemplates, loadExistingStudy]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEdit && !selectedTemplateId) { setError('템플릿을 선택해주세요.'); return; }
    if (!title.trim()) { setError('제목을 입력해주세요.'); return; }

    setSubmitting(true);
    setError('');
    try {
      if (isEdit) {
        await studyApi.update(id, { title, description: description || undefined, tags });
        navigate(`/studies/${id}`);
      } else {
        const res = await studyApi.create({
          template_id: selectedTemplateId,
          title,
          description: description || undefined,
          tags,
        });
        navigate(`/studies/${res.data.id}`);
      }
    } catch (e) {
      setError(e.message || '저장에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="sfp-loading">불러오는 중...</div>;
  }

  return (
    <div className="sfp-page">
      <div className="sfp-header">
        <button className="sfp-back-btn" onClick={() => navigate(-1)}>← 뒤로가기</button>
        <h1 className="sfp-title">{isEdit ? '스터디 수정' : '스터디 작성'}</h1>
      </div>

      {error && <div className="sfp-error">{error}</div>}

      <form className="sfp-form" onSubmit={handleSubmit}>
        {/* 템플릿 선택 (작성 모드만) */}
        {!isEdit && (
          <div className="sfp-field">
            <label className="sfp-label">사용할 템플릿 <span className="sfp-required">*</span></label>
            <select
              className="sfp-select"
              value={selectedTemplateId}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
              required
            >
              <option value="">템플릿 선택...</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.name} v{t.version}</option>
              ))}
            </select>
          </div>
        )}

        {/* 제목 */}
        <div className="sfp-field">
          <label className="sfp-label">제목 <span className="sfp-required">*</span></label>
          <input
            type="text"
            className="sfp-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="스터디 제목 (예: 삼성전자 분석)"
            required
          />
        </div>

        {/* 설명 */}
        <div className="sfp-field">
          <label className="sfp-label">설명</label>
          <textarea
            className="sfp-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="스터디 그룹 설명 (선택)"
            rows={3}
          />
        </div>

        {/* 태그 */}
        <div className="sfp-field">
          <label className="sfp-label">태그</label>
          <TagInput value={tags} onChange={setTags} />
        </div>

        {/* 버튼 */}
        <div className="sfp-actions">
          <button
            type="button"
            className="sfp-btn sfp-btn-cancel"
            onClick={() => navigate(-1)}
          >
            취소
          </button>
          <button
            type="submit"
            className="sfp-btn sfp-btn-submit"
            disabled={submitting}
          >
            {submitting ? '저장 중...' : (isEdit ? '수정 완료' : '작성 완료')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default StudyFormPage;
